'use client';

import { CompositeItem } from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import {
  Fragment,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { Slot, Slottable } from '../slot';
import { TabsRootContext } from './context/tabs-root.context';
import { tabsTabRecipe } from './styles/tabs-tab.styles';
import { TabsTabIcon } from './tabs-tab-icon';
import type { TabsTabPropsInterface } from './types/tabs-tab.types';

/**
 * @since 10.0.0
 */
export const TabsTab = forwardRef<HTMLButtonElement, TabsTabPropsInterface>(
  (props: TabsTabPropsInterface, forwardedRef) => {
    const {
      selected,
      setSelected: setContextSelected,
      appearance,
      fluid = false,
      ...tabsContext
    } = useContext(TabsRootContext);

    const {
      value,
      icon,
      asChild = false,
      children,
      className,
      css: cssProp,
      disabled = tabsContext.disabled ?? false,
      shouldAwaitInteractionResponse = true,
      onClick,
      ...rest
    } = props;

    const onTabClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      async (event) => {
        if (!disabled) {
          if (shouldAwaitInteractionResponse) {
            await interactionResponse();
          }

          setContextSelected?.(value);
          onClick?.(event);
        }
      },
      [disabled, onClick, value, shouldAwaitInteractionResponse, setContextSelected],
    );

    const isSelected = useMemo(() => selected === value, [selected, value]);

    const styles = css(
      tabsTabRecipe.raw({ appearance, disabled, fluid, selected: isSelected }),
      cssProp,
    );

    const Component = asChild ? Slot : 'button';

    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    return (
      <CompositeItem
        render={(compositeProps) => {
          // Don't use the tabIndex provided by Composite's roving tabindex.
          // Selection state is the authoritative source for which tab is tabbable.
          const { tabIndex: _compositeTabIndex, ...restCompositeProps } = compositeProps;

          return (
            <Component
              role="tab"
              ref={forwardedRef}
              id={`${value}-tab`}
              aria-selected={isSelected}
              aria-controls={`${value}-panel`}
              data-c11n-component="Tabs.Tab"
              data-c11n-version={__C11N_VERSION__}
              className={cx('c11n', 'group', styles, className)}
              onClick={onTabClick}
              disabled={!asChild && disabled ? true : undefined}
              type={asChild ? undefined : 'button'}
              {...restCompositeProps}
              data-active={null}
              {...rest}
              {...(asChild && disabled
                ? disabledInteractionsProps
                : { tabIndex: isSelected && !disabled ? 0 : -1 })}
            >
              <Slottable child={children}>
                {(slottable) => (
                  <Fragment>
                    {icon ? <TabsTabIcon>{icon}</TabsTabIcon> : null}
                    <span>{slottable}</span>
                  </Fragment>
                )}
              </Slottable>
            </Component>
          );
        }}
      />
    );
  },
);

TabsTab.displayName = 'Tabs.Tab';
